import { Box } from '@mui/material';
import { Button } from 'system/atoms';
import { SearchInput } from 'components/Inputs/SearchInput';
import React, { useMemo, useState } from 'react';
import { TableV2 } from 'components/Table/TableV2';
import { useQuery } from 'services/api/hooks';
import { AtsJobInput, CandidateBatchGroup } from 'services/candidate/types';
import {
  getCandidateBatchGroupByOrganization,
  processCandidateBatches
} from 'services/candidate/api';
import { useRecoilValue } from 'recoil';
import { SxProps } from '@mui/material/styles';
import { NextButton } from 'components/NextButton';
import { useConfirmationModal } from 'services/modals/hooks';
import { useDrawer } from 'services/drawer/hooks';
import { organizationAtom } from 'services/organization/atom';
import { splitCandidateBatchesRequestIntoChucks } from 'services/candidate/utils/splitCandidateBatchesRequestIntoChucks';
import { ToastType, useToast } from 'services/toast';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { Loading } from 'common/ui/Loading';
import { getRulesetColumns } from '../../utils/getRulesetColumns';
import { useApplyCandidateBatchGroupMutation } from 'generated-graphql/hooks/apollo-hooks';

type Props = {
  atsJobs: AtsJobInput[];
  onSkip: () => void;
  onCreateRuleset: () => void;
  candidateBatchId?: string;
};

const container: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  mb: 2
};

export const ATSSelectRulesetStep: React.FC<Props> = ({
  atsJobs,
  onSkip,
  onCreateRuleset,
  candidateBatchId
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [candidateBatchGroup, setCandidateBatchGroup] =
    useState<CandidateBatchGroup>();
  const [searchValue, setSearchValue] = useState('');
  const organization = useRecoilValue(organizationAtom);
  const navigate = useNavigate();
  const { showModal } = useConfirmationModal();
  const { showToast } = useToast();
  const { openDrawer } = useDrawer();
  const [applyCandidateBatchGroup] = useApplyCandidateBatchGroupMutation();

  const isEditingDraft = !!candidateBatchId;
  const numberOfJobs = isEditingDraft ? 1 : atsJobs.length;
  const isSingleJob = numberOfJobs === 1;

  const { data, isLoading } = useQuery<{
    items: CandidateBatchGroup[];
  }>(getCandidateBatchGroupByOrganization, {
    organizationId: organization?.id
  });

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!searchValue) return data.items;

    return data.items.filter((item) =>
      item.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [JSON.stringify(data), searchValue]);

  const handleSkip = async () => {
    // NOTE: This is a safe guard
    if (!isSingleJob) {
      showToast({
        message: 'You cannot skip when selecting multiple jobs',
        type: ToastType.Error
      });
      return;
    }

    onSkip();
  };

  const handleConfirmSelection = async () => {
    if (!candidateBatchGroup) {
      showToast({
        message: 'Please select a ruleset',
        type: ToastType.Error
      });
      return;
    }

    showModal({
      title: 'Apply ruleset',
      message: `Start analysing ${numberOfJobs} job${
        numberOfJobs > 1 ? 's' : ''
      } with ${candidateBatchGroup.name}?`,
      onConfirm: async () => {
        setIsSubmitting(true);

        try {
          if (isEditingDraft) {
            // For draft analyses, call applyCandidateBatchGroup with launchDraft flag
            await applyCandidateBatchGroup({
              variables: {
                input: {
                  candidateBatchGroupId: candidateBatchGroup.id,
                  candidateBatchIds: [candidateBatchId],
                  launchDraft: true
                }
              }
            });

            navigate(`${Paths.ANALYSE}/${candidateBatchId}`);

            showToast({
              message: `Ruleset ${candidateBatchGroup.name} applied to draft analysis`,
              type: ToastType.Success
            });
          } else {
            // For new analyses, process the ATS jobs
            const atsJobChucks =
              splitCandidateBatchesRequestIntoChucks(atsJobs);

            await Promise.all(
              atsJobChucks.map((atsJobs) =>
                processCandidateBatches({
                  atsJobs,
                  candidateBatchGroupId: candidateBatchGroup.id
                })
              )
            );

            navigate(`${Paths.ANALYSE_RULESETS}/${candidateBatchGroup.id}`);

            showToast({
              message: `Your jobs have been added to ${candidateBatchGroup.name}`,
              type: ToastType.Success
            });
          }
        } catch {
          showToast({
            title: 'Something went wrong',
            message: 'We could not apply this ruleset. Please try again.',
            type: ToastType.Error
          });
        } finally {
          setIsSubmitting(false);
        }
      },
      confirmationText: 'OK'
    });
  };

  const groupsColumns = getRulesetColumns({ openDrawer });

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Box>
      <Box sx={container}>
        <Box>
          <SearchInput
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onClear={() => setSearchValue('')}
            placeholder="Search"
          />
        </Box>
        <Box display="flex" gap={2}>
          {isSingleJob && (
            <Button variant="secondary" onClick={handleSkip}>
              Skip
            </Button>
          )}
          {!isSingleJob && (
            <Button variant="secondary" onClick={onCreateRuleset}>
              Create ruleset
            </Button>
          )}
          <NextButton
            handleNext={handleConfirmSelection}
            disabled={isSubmitting}
            nextText="Confirm selection"
            isError={!candidateBatchGroup}
            errorMessage="Choose a ruleset to continue."
            errorTitle="No ruleset selected"
          />
        </Box>
      </Box>

      <TableV2
        data={filteredData}
        columns={groupsColumns}
        selectableRows
        selectableRowsSingle
        onSelectedRowsChange={(state) => {
          setCandidateBatchGroup(state.selectedRows[0]);
        }}
      />
    </Box>
  );
};
